import { useState } from "react";
import type { KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Header } from "../components/Header";
import { useAuthDialog } from "../components/AuthDialog";
import { getSession, logout } from "../lib/api";
import { getProfile, getProfileActivity, updateProfile } from "../lib/account";
import type { ActivityKind, UserActivityItem, UserProfile } from "../lib/account";
import type { PublicUser } from "../types";
import "./profile.css";

const activityTabs: { kind: ActivityKind; label: string; empty: string }[] = [
  { kind: "likes", label: "Likes", empty: "You have not liked any posts yet." },
  { kind: "dislikes", label: "Dislikes", empty: "You have not disliked any posts yet." },
  { kind: "comments", label: "Comments", empty: "You have not posted any comments yet." }
];
const dateFormatter = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" });

function ActivityIcon({ kind }: { kind: ActivityKind }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {kind === "comments" ? <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8z" /> : (
        <g transform={kind === "dislikes" ? "rotate(180 12 12)" : undefined}>
          <path d="M7 10v11H3V10h4Zm0 0 5-8a3 3 0 0 1 2 4l-1 4h6a2 2 0 0 1 2 2.4l-1.5 7A2 2 0 0 1 17.5 21H7" />
        </g>
      )}
    </svg>
  );
}

function ProfileAvatar({ name, url }: { name: string; url: string | null }) {
  const [failed, setFailed] = useState(false);
  const initials = name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "?";
  return (
    <div className="profile-avatar" aria-hidden="true">
      {url && !failed ? <img src={url} alt="" width="80" height="80" referrerPolicy="no-referrer" onError={() => setFailed(true)} /> : <span>{initials}</span>}
    </div>
  );
}

function ProfileEditor({ profile }: { profile: UserProfile }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(profile.display_name);
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url ?? "");
  const saveMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (saved) => {
      queryClient.setQueryData(["profile", profile.id], saved);
      setName(saved.display_name);
      setAvatarUrl(saved.avatar_url ?? "");
      void queryClient.invalidateQueries({ queryKey: ["comments"] });
    }
  });
  const changed = name.trim() !== profile.display_name || (avatarUrl.trim() || null) !== profile.avatar_url;

  return (
    <form className="profile-details" onSubmit={(event) => {
      event.preventDefault();
      saveMutation.mutate({ display_name: name.trim(), avatar_url: avatarUrl.trim() || null });
    }}>
      <div className="profile-identity">
        <ProfileAvatar key={profile.avatar_url} name={profile.display_name} url={profile.avatar_url} />
        <div><span className="profile-eyebrow">Your space</span><h2>{profile.display_name || "Your profile"}</h2><p>How you appear in comments.</p></div>
      </div>
      <label htmlFor="profile-name">Display name</label>
      <input id="profile-name" name="display_name" autoComplete="nickname" value={name} required maxLength={80} disabled={saveMutation.isPending} onChange={(event) => { setName(event.target.value); saveMutation.reset(); }} />
      <label htmlFor="profile-email">Account email</label>
      <input id="profile-email" type="email" autoComplete="email" value={profile.email} readOnly aria-describedby="profile-email-hint" />
      <p className="profile-field-hint" id="profile-email-hint">Managed by your sign-in account. Not shown in comments.</p>
      <label htmlFor="profile-avatar-url">Photo URL <span>(optional)</span></label>
      <input id="profile-avatar-url" name="avatar_url" type="url" pattern="https://.*" maxLength={2048} placeholder="https://…" value={avatarUrl} disabled={saveMutation.isPending} aria-describedby="profile-avatar-hint" onChange={(event) => { setAvatarUrl(event.target.value); saveMutation.reset(); }} />
      <p className="profile-field-hint" id="profile-avatar-hint">Use an HTTPS image URL or leave blank to show your initials.</p>
      <button className="profile-button profile-button-primary" type="submit" disabled={saveMutation.isPending || !changed || !name.trim()}>{saveMutation.isPending ? "Saving…" : "Save changes"}</button>
      {saveMutation.isSuccess ? <p className="profile-feedback profile-feedback-success" role="status">Profile updated.</p> : null}
      {saveMutation.isError ? <p className="profile-feedback profile-feedback-error" role="alert">{saveMutation.error instanceof Error ? saveMutation.error.message : "Unable to save profile. Try again."}</p> : null}
    </form>
  );
}

function ActivityItem({ item, kind }: { item: UserActivityItem; kind: ActivityKind }) {
  const date = new Date(item.created_at);
  return (
    <li className="profile-activity-item">
      <span className={`profile-activity-icon profile-activity-icon-${kind}`}><ActivityIcon kind={kind} /></span>
      <div>
        <p className="profile-activity-caption">{kind === "comments" ? "You commented" : kind === "likes" ? "You liked this" : "You disliked this"}</p>
        {item.post ? <Link to="/post/$slug" params={{ slug: item.post.slug }}>{item.post.title}</Link> : <span className="profile-unavailable">Article unavailable</span>}
        {kind === "comments" && item.body ? <blockquote>{item.body}</blockquote> : null}
        {Number.isNaN(date.valueOf()) ? null : <time dateTime={item.created_at}>{dateFormatter.format(date)}</time>}
      </div>
    </li>
  );
}

function ProfileActivity({ userId }: { userId: string }) {
  const [kind, setKind] = useState<ActivityKind>("likes");
  const [page, setPage] = useState(1);
  const activityQuery = useQuery({
    queryKey: ["profile-activity", userId, kind, page],
    queryFn: () => getProfileActivity(kind, page),
    retry: 1
  });
  function selectTab(next: ActivityKind) { setKind(next); setPage(1); }
  function handleTabKeys(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next: number;
    if (event.key === "ArrowRight") next = (index + 1) % activityTabs.length;
    else if (event.key === "ArrowLeft") next = (index + activityTabs.length - 1) % activityTabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = activityTabs.length - 1;
    else return;
    event.preventDefault();
    selectTab(activityTabs[next].kind);
    document.getElementById(`profile-tab-${activityTabs[next].kind}`)?.focus();
  }
  return (
    <section className="profile-activity" aria-labelledby="profile-activity-title">
      <div className="profile-section-heading"><span className="profile-eyebrow">Your activity</span><h2 id="profile-activity-title">Activity</h2><p>Revisit the articles and conversations you took part in.</p></div>
      <div className="profile-tabs" role="tablist" aria-label="Activity type">
        {activityTabs.map((tab, index) => <button key={tab.kind} type="button" role="tab" id={`profile-tab-${tab.kind}`} aria-controls="profile-activity-panel" aria-selected={kind === tab.kind} tabIndex={kind === tab.kind ? 0 : -1} onClick={() => selectTab(tab.kind)} onKeyDown={(event) => handleTabKeys(event, index)}><ActivityIcon kind={tab.kind} />{tab.label}</button>)}
      </div>
      <div id="profile-activity-panel" className="profile-activity-panel" role="tabpanel" aria-labelledby={`profile-tab-${kind}`} tabIndex={0} aria-busy={activityQuery.isPending}>
        {activityQuery.isPending ? <p className="profile-state" role="status">Loading your activity…</p> : activityQuery.isError ? <div className="profile-state"><p role="alert">Unable to load activity.</p><button className="profile-button" type="button" onClick={() => void activityQuery.refetch()}>Try again</button></div> : activityQuery.data.items.length === 0 ? <div className="profile-state profile-empty"><span className="profile-empty-icon"><ActivityIcon kind={kind} /></span><h3>{activityTabs.find((tab) => tab.kind === kind)?.empty}</h3><p>Your likes and comments are saved here.</p><Link className="profile-button" to="/blog">Explore the blog <span aria-hidden="true">→</span></Link></div> : <ul className="profile-activity-list">{activityQuery.data.items.map((item) => <ActivityItem key={item.id} item={item} kind={kind} />)}</ul>}
      </div>
      {page > 1 || activityQuery.data?.hasMore ? <nav className="profile-pagination" aria-label="Activity pagination"><button className="profile-button" type="button" disabled={page === 1 || activityQuery.isPending} onClick={() => setPage((current) => current - 1)}>Previous</button><span aria-live="polite">Page {page}</span><button className="profile-button" type="button" disabled={!activityQuery.data?.hasMore || activityQuery.isPending} onClick={() => setPage((current) => current + 1)}>Next</button></nav> : null}
    </section>
  );
}

function SignedInProfile({ session }: { session: PublicUser }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const profileQuery = useQuery({ queryKey: ["profile", session.id], queryFn: getProfile, retry: 1 });
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      await queryClient.cancelQueries();
      queryClient.clear();
      queryClient.setQueryData(["session"], null);
      await navigate({ to: "/" });
    }
  });
  return (
    <>
      <div className="profile-page-heading"><div><span className="profile-eyebrow">/profile</span><h1>Your profile.</h1><p>Edit your profile and keep track of your activity.</p></div><div className="profile-heading-actions">{session.isAdmin ? <Link className="profile-button" to="/admin">Open backoffice</Link> : null}<button className="profile-button" type="button" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>{logoutMutation.isPending ? "Signing out…" : "Sign out"}</button></div></div>
      {logoutMutation.isError ? <p className="profile-feedback profile-feedback-error" role="alert">Unable to sign out. Try again.</p> : null}
      <div className="profile-layout">
        <section className="profile-card" aria-label="Edit profile" aria-busy={profileQuery.isPending}>
          {profileQuery.isPending ? <p className="profile-state" role="status">Loading profile…</p> : profileQuery.isError ? <div className="profile-state"><p role="alert">Unable to load profile.</p><button className="profile-button" type="button" onClick={() => void profileQuery.refetch()}>Try again</button></div> : <ProfileEditor key={profileQuery.data.id} profile={profileQuery.data} />}
        </section>
        <ProfileActivity userId={session.id} />
      </div>
    </>
  );
}

export function ProfilePage() {
  const { openLogin } = useAuthDialog();
  const sessionQuery = useQuery({ queryKey: ["session"], queryFn: getSession });
  return (
    <><Header /><main className="profile-shell">{sessionQuery.isPending ? <div className="profile-gate" role="status">Checking your session…</div> : sessionQuery.isError ? <div className="profile-gate"><h1>Unable to verify your session.</h1><p>Try again to access your profile.</p><button className="profile-button" type="button" onClick={() => void sessionQuery.refetch()}>Try again</button></div> : sessionQuery.data ? <SignedInProfile key={sessionQuery.data.id} session={sessionQuery.data} /> : <div className="profile-gate"><span className="profile-eyebrow">Your space</span><h1>One account. Your conversations.</h1><p>Sign in to edit your profile and view your likes and comments.</p><button className="profile-button profile-button-primary" type="button" onClick={openLogin}>Sign in with GitHub or LinkedIn</button><Link to="/blog">Keep exploring the blog <span aria-hidden="true">→</span></Link></div>}</main></>
  );
}
